// InventoryTable.js - Enhanced data table with column filters, selection, and resize
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ENTITY_TYPE_CONFIG, SEVERITY_CONFIG } from '../../services/inventoryService';
import './InventoryTable.css';

// Default column widths
const DEFAULT_COLUMN_WIDTHS = {
    checkbox: 40,
    type: 120,
    entity: 0, // flex
    asset: 150,
    severity: 100,
    state: 90,
    firstSeen: 110,
    lastSeen: 110
};

// Load saved column widths from localStorage
const loadColumnWidths = () => {
    try {
        const saved = localStorage.getItem('inventory-column-widths');
        return saved ? { ...DEFAULT_COLUMN_WIDTHS, ...JSON.parse(saved) } : DEFAULT_COLUMN_WIDTHS;
    } catch {
        return DEFAULT_COLUMN_WIDTHS;
    }
};

function InventoryTable({
    items,
    onRowClick,
    onLoadMore,
    hasMore,
    loading,
    totalCount,
    sort,
    order,
    onSortChange,
    // New props for filtering
    filters,
    facets,
    assets,
    onFilterChange,
    // Selection props
    selectedIds,
    onSelectionChange
}) {
    const [columnWidths, setColumnWidths] = useState(loadColumnWidths);
    const [resizing, setResizing] = useState(null);
    const [openFilter, setOpenFilter] = useState(null);
    const tableRef = useRef(null);
    const filterRefs = useRef({});

    // Save column widths to localStorage
    useEffect(() => {
        localStorage.setItem('inventory-column-widths', JSON.stringify(columnWidths));
    }, [columnWidths]);

    // Close filter dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (openFilter && filterRefs.current[openFilter] &&
                !filterRefs.current[openFilter].contains(e.target)) {
                setOpenFilter(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openFilter]);

    // Handle resize
    const handleResizeStart = useCallback((column, e) => {
        e.preventDefault();
        e.stopPropagation();
        setResizing({ column, startX: e.clientX, startWidth: columnWidths[column] });
    }, [columnWidths]);

    useEffect(() => {
        if (!resizing) return;

        const handleMouseMove = (e) => {
            const diff = e.clientX - resizing.startX;
            const newWidth = Math.max(50, resizing.startWidth + diff);
            setColumnWidths(prev => ({ ...prev, [resizing.column]: newWidth }));
        };

        const handleMouseUp = () => {
            setResizing(null);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [resizing]);

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        const now = new Date();
        const diff = now - date;

        if (diff < 24 * 60 * 60 * 1000) {
            const hours = Math.floor(diff / (60 * 60 * 1000));
            if (hours < 1) {
                const mins = Math.floor(diff / (60 * 1000));
                return `${mins}m ago`;
            }
            return `${hours}h ago`;
        }

        if (diff < 7 * 24 * 60 * 60 * 1000) {
            const days = Math.floor(diff / (24 * 60 * 60 * 1000));
            return `${days}d ago`;
        }

        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
        });
    };

    // Get configs
    const getTypeConfig = (type) => ENTITY_TYPE_CONFIG[type] || { label: type, icon: '📦', color: '#888' };
    const getSeverityConfig = (severity) => SEVERITY_CONFIG[severity?.toLowerCase()] || null;

    // Selection handlers
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            onSelectionChange?.(items.map(item => item.entityId));
        } else {
            onSelectionChange?.([]);
        }
    };

    const handleSelectRow = (entityId, e) => {
        e.stopPropagation();
        const newSelection = selectedIds?.includes(entityId)
            ? selectedIds.filter(id => id !== entityId)
            : [...(selectedIds || []), entityId];
        onSelectionChange?.(newSelection);
    };

    const isAllSelected = items.length > 0 && selectedIds?.length === items.length;
    const isSomeSelected = selectedIds?.length > 0 && selectedIds?.length < items.length;

    // Filter dropdown component
    const FilterDropdown = ({ column, options, currentValue, label }) => {
        const isOpen = openFilter === column;
        const hasActiveFilter = currentValue && currentValue !== 'all';

        return (
            <div
                className="column-filter"
                ref={el => filterRefs.current[column] = el}
            >
                <button
                    className={`filter-trigger ${hasActiveFilter ? 'active' : ''}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        setOpenFilter(isOpen ? null : column);
                    }}
                    title={`Filter by ${label}`}
                >
                    <span className="filter-icon">▼</span>
                </button>
                {isOpen && (
                    <div className="filter-dropdown">
                        <div className="filter-dropdown-header">{label}</div>
                        <div className="filter-options">
                            <label className="filter-option">
                                <input
                                    type="radio"
                                    name={`filter-${column}`}
                                    checked={!currentValue || currentValue === 'all'}
                                    onChange={() => {
                                        onFilterChange?.(column, 'all');
                                        setOpenFilter(null);
                                    }}
                                />
                                <span>All</span>
                            </label>
                            {options.map(option => (
                                <label key={option.value} className="filter-option">
                                    <input
                                        type="radio"
                                        name={`filter-${column}`}
                                        checked={currentValue === option.value}
                                        onChange={() => {
                                            onFilterChange?.(column, option.value);
                                            setOpenFilter(null);
                                        }}
                                    />
                                    <span className="option-content">
                                        {option.icon && <span className="option-icon">{option.icon}</span>}
                                        <span className="option-label">{option.label}</span>
                                        {option.count !== undefined && (
                                            <span className="option-count">({option.count})</span>
                                        )}
                                    </span>
                                </label>
                            ))}
                        </div>
                        {hasActiveFilter && (
                            <button
                                className="filter-clear"
                                onClick={() => {
                                    onFilterChange?.(column, 'all');
                                    setOpenFilter(null);
                                }}
                            >
                                Clear Filter
                            </button>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // Build filter options
    const typeOptions = Object.entries(ENTITY_TYPE_CONFIG).map(([type, config]) => ({
        value: type,
        label: config.label,
        icon: config.icon,
        count: facets?.byType?.[type] || 0
    }));

    const severityOptions = Object.entries(SEVERITY_CONFIG).map(([sev, config]) => ({
        value: sev,
        label: config.label,
        count: facets?.bySeverity?.[sev] || 0
    }));

    const stateOptions = [
        { value: 'active', label: 'Active', count: facets?.byState?.active || 0 },
        { value: 'resolved', label: 'Resolved', count: facets?.byState?.resolved || 0 }
    ];

    const assetOptions = (assets || []).map(asset => ({
        value: asset.assetId || asset.rowKey,
        label: asset.value || asset.assetValue,
        icon: asset.assetType === 'domain' ? '🌐' : '🔢'
    }));

    // Sortable header with optional filter
    const ColumnHeader = ({ column, field, children, sortable = false, filterable = false, filterOptions, filterColumn, filterLabel }) => {
        const width = columnWidths[column];
        const style = width ? { width: `${width}px`, minWidth: `${width}px` } : {};

        return (
            <th
                className={`${sortable ? 'sortable' : ''} ${resizing?.column === column ? 'resizing' : ''}`}
                style={style}
                onClick={sortable ? () => onSortChange(field) : undefined}
            >
                <div className="th-content">
                    <span className="th-label">{children}</span>
                    {sortable && (
                        <span className={`sort-indicator ${sort === field ? 'active' : ''}`}>
                            {sort === field ? (order === 'desc' ? '↓' : '↑') : '⇅'}
                        </span>
                    )}
                    {filterable && (
                        <FilterDropdown
                            column={filterColumn || column}
                            options={filterOptions}
                            currentValue={filters?.[filterColumn || column]}
                            label={filterLabel || children}
                        />
                    )}
                </div>
                {column !== 'lastSeen' && (
                    <div
                        className="resize-handle"
                        onMouseDown={(e) => handleResizeStart(column, e)}
                    />
                )}
            </th>
        );
    };

    return (
        <div className="inventory-table-container" ref={tableRef}>
            {/* Results Summary */}
            <div className="table-summary">
                <span className="summary-count">
                    Showing <strong>{items.length.toLocaleString()}</strong> of{' '}
                    <strong>{totalCount.toLocaleString()}</strong> entities
                </span>
            </div>

            {/* Table Wrapper */}
            <div className="table-wrapper">
                <table className={`inventory-table ${resizing ? 'resizing' : ''}`}>
                    <thead>
                        <tr>
                            {/* Checkbox Column */}
                            <th className="th-checkbox" style={{ width: columnWidths.checkbox }}>
                                <input
                                    type="checkbox"
                                    checked={isAllSelected}
                                    ref={el => {
                                        if (el) el.indeterminate = isSomeSelected;
                                    }}
                                    onChange={handleSelectAll}
                                    title="Select all"
                                />
                            </th>

                            <ColumnHeader
                                column="type"
                                field="type"
                                sortable
                                filterable
                                filterOptions={typeOptions}
                                filterColumn="type"
                                filterLabel="Entity Type"
                            >
                                Type
                            </ColumnHeader>

                            <ColumnHeader column="entity">
                                Entity
                            </ColumnHeader>

                            <ColumnHeader
                                column="asset"
                                filterable
                                filterOptions={assetOptions}
                                filterColumn="assetId"
                                filterLabel="Source Asset"
                            >
                                Asset
                            </ColumnHeader>

                            <ColumnHeader
                                column="severity"
                                field="severity"
                                sortable
                                filterable
                                filterOptions={severityOptions}
                                filterColumn="severity"
                                filterLabel="Severity"
                            >
                                Severity
                            </ColumnHeader>

                            <ColumnHeader
                                column="state"
                                field="state"
                                sortable
                                filterable
                                filterOptions={stateOptions}
                                filterColumn="state"
                                filterLabel="State"
                            >
                                State
                            </ColumnHeader>

                            <ColumnHeader column="firstSeen" field="firstSeenAt" sortable>
                                First Seen
                            </ColumnHeader>

                            <ColumnHeader column="lastSeen" field="lastSeenAt" sortable>
                                Last Seen
                            </ColumnHeader>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item) => {
                            const typeConfig = getTypeConfig(item.type);
                            const severityConfig = getSeverityConfig(item.severity);
                            const isSelected = selectedIds?.includes(item.entityId);

                            return (
                                <tr
                                    key={item.entityId}
                                    onClick={() => onRowClick(item)}
                                    className={`
                                        ${item.isSuppressed ? 'suppressed' : ''}
                                        ${item.state === 'resolved' ? 'resolved' : ''}
                                        ${isSelected ? 'selected' : ''}
                                    `}
                                >
                                    {/* Checkbox */}
                                    <td className="cell-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={(e) => handleSelectRow(item.entityId, e)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </td>

                                    {/* Type */}
                                    <td className="cell-type" style={{ width: columnWidths.type }}>
                                        <span className="type-badge" style={{ color: typeConfig.color }}>
                                            <span className="type-icon">{typeConfig.icon}</span>
                                            <span className="type-label">{item.type}</span>
                                        </span>
                                    </td>

                                    {/* Entity Key/Display */}
                                    <td className="cell-entity">
                                        <div className="entity-info">
                                            <span className="entity-value" title={item.key}>
                                                {item.displayValue || item.key}
                                            </span>
                                            {item.displayValue && item.displayValue !== item.key && (
                                                <span className="entity-key" title={item.key}>
                                                    {item.key.length > 50 ? item.key.substring(0, 50) + '...' : item.key}
                                                </span>
                                            )}
                                        </div>
                                    </td>

                                    {/* Asset */}
                                    <td className="cell-asset" style={{ width: columnWidths.asset }}>
                                        {item.assetValue ? (
                                            <span className="asset-value">{item.assetValue}</span>
                                        ) : (
                                            <span className="no-value">-</span>
                                        )}
                                    </td>

                                    {/* Severity */}
                                    <td className="cell-severity" style={{ width: columnWidths.severity }}>
                                        {severityConfig ? (
                                            <span
                                                className="severity-badge"
                                                style={{
                                                    color: severityConfig.color,
                                                    backgroundColor: severityConfig.bgColor
                                                }}
                                            >
                                                {severityConfig.label}
                                            </span>
                                        ) : (
                                            <span className="no-value">-</span>
                                        )}
                                    </td>

                                    {/* State */}
                                    <td className="cell-state" style={{ width: columnWidths.state }}>
                                        <span className={`state-badge state-${item.state}`}>
                                            {item.state}
                                        </span>
                                        {item.isSuppressed && (
                                            <span className="suppressed-badge" title="Suppressed">🔇</span>
                                        )}
                                    </td>

                                    {/* First Seen */}
                                    <td className="cell-date" style={{ width: columnWidths.firstSeen }}>
                                        {formatDate(item.firstSeenAt)}
                                    </td>

                                    {/* Last Seen */}
                                    <td className="cell-date" style={{ width: columnWidths.lastSeen }}>
                                        {formatDate(item.lastSeenAt)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Load More Button */}
            {hasMore && (
                <div className="load-more-container">
                    <button
                        className="btn-load-more"
                        onClick={onLoadMore}
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span className="loading-spinner-small"></span>
                                Loading...
                            </>
                        ) : (
                            <>
                                Load More
                                <span className="load-more-icon">↓</span>
                            </>
                        )}
                    </button>
                </div>
            )}
        </div>
    );
}

export default InventoryTable;
